import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Send,
  FileText,
  Loader2,
  MessageCircle,
  LogOut,
  Menu,
  X,
  Trash2,
  Plus,
  Clock,
  Globe,
  ArrowLeft
} from 'lucide-react';
import { useKeycloak } from '@react-keycloak/web';
import { ChartRenderer } from './ChartRenderer';
import {
  ApiError,
  API_BASE_URL,
  clearSelectedBot,
  deleteConversation as deleteConversationApi,
  getConversation,
  getConversations,
  getSelectedBotId,
  getSelectedBotName,
  queryBot,
} from './api';
import { getBotDisplayConfig } from './botConfig';
import './App.css';

interface Source {
  document: string;
  page: number | string;
  snippet: string;
  type?: 'document' | 'web';
  url?: string;
}

interface ChartData {
  type: 'bar' | 'line' | 'pie';
  title?: string;
  data: any[];
  xKey?: string;
  yKey?: string;
  colors?: string[];
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  sources?: Source[];
  chart?: ChartData;
  timestamp: string;
  feedback?: 'like' | 'dislike' | null;
  isEditing?: boolean;
  isClarification?: boolean;
}

interface Conversation {
  id: string;
  title: string;
  created_at: string;
  last_updated: string;
  message_count: number;
}

function ChatInterface() {
  const { keycloak, initialized } = useKeycloak();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [topK, setTopK] = useState(2);
  const [enableWebSearch, setEnableWebSearch] = useState(false);
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [currentFeedbackIndex, setCurrentFeedbackIndex] = useState<number | null>(null);
  const [editingMessageIndex, setEditingMessageIndex] = useState<number | null>(null);
  const [editedQuery, setEditedQuery] = useState('');
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [initTimeout, setInitTimeout] = useState(false);
  const [selectedBot, setSelectedBot] = useState(() => ({
    id: getSelectedBotId(),
    name: getSelectedBotName(),
  }));

  const token = keycloak.token;
  const config = getBotDisplayConfig(
    selectedBot.id || 'finance-report',
    selectedBot.name || undefined
  );

  // Timeout fallback if Keycloak doesn't initialize
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!initialized) {
        console.warn('Keycloak initialization timeout - proceeding without auth');
        setInitTimeout(true);
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timer);
  }, [initialized]);

  const redirectToSelection = useCallback(
    (clearBot: boolean) => {
      if (clearBot) {
        clearSelectedBot();
        setSelectedBot({ id: null, name: null });
      }

      navigate('/select', { replace: true });
    },
    [navigate]
  );

  const handleBotAccessError = useCallback(
    (error: unknown) => {
      let status: number | undefined;
      let message = '';

      if (error instanceof ApiError) {
        status = error.status;
        message = error.message.toLowerCase();
      } else if (axios.isAxiosError(error)) {
        status = error.response?.status;
        const detail =
          typeof error.response?.data?.detail === 'string'
            ? error.response.data.detail
            : error.message;
        message = detail.toLowerCase();
      } else {
        return false;
      }

      if (status === 400 && message.includes('bot_id')) {
        redirectToSelection(false);
        return true;
      }

      if (status === 403) {
        redirectToSelection(true);
        return true;
      }

      return false;
    },
    [redirectToSelection]
  );

  const loadConversations = useCallback(async () => {
    if (!token || !selectedBot.id) {
      return;
    }

    try {
      setLoadingHistory(true);
      const response = await getConversations(token, selectedBot.id);
      setConversations(response.conversations || []);
    } catch (error) {
      if (handleBotAccessError(error)) {
        return;
      }

      console.error('Error loading conversations:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, [handleBotAccessError, selectedBot.id, token]);

  const loadConversation = async (conversationId: string) => {
    if (!token || !selectedBot.id) {
      return;
    }

    try {
      const data = await getConversation(token, selectedBot.id, conversationId);

      setMessages(data.messages);
      setCurrentConversationId(conversationId);
      setSidebarOpen(false);
    } catch (error) {
      if (handleBotAccessError(error)) {
        return;
      }

      console.error('Error loading conversation:', error);
    }
  };

  useEffect(() => {
    if (initialized && keycloak.authenticated && !selectedBot.id) {
      navigate('/select', { replace: true });
      return;
    }

    if (initialized && keycloak.authenticated && selectedBot.id) {
      loadConversations();
    }
  }, [
    initialized,
    keycloak.authenticated,
    loadConversations,
    navigate,
    selectedBot.id,
  ]);

  const deleteConversation = async (
    conversationId: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;
    if (!token || !selectedBot.id) return;

    try {
      await deleteConversationApi(token, selectedBot.id, conversationId);

      if (currentConversationId === conversationId) {
        setMessages([]);
        setCurrentConversationId(null);
      }

      void loadConversations();
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setCurrentConversationId(null);
    setSidebarOpen(false);
  };

  const switchBot = () => {
    setShowSwitchConfirm(true);
  };

  const confirmSwitchBot = () => {
    setShowSwitchConfirm(false);
    navigate('/select');
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const toggleSources = (index: number) => {
    const el = document.getElementById(`sources-${index}`);
    el?.classList.toggle('expanded');
  };

  const groupConversationsByDate = () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(today.getDate() - 7);

    const groups: Record<string, Conversation[]> = {
      Today: [],
      Yesterday: [],
      'Last 7 Days': [],
      Older: [],
    };

    conversations.forEach((conv) => {
      const date = new Date(conv.last_updated);
      if (date.toDateString() === today.toDateString()) {
        groups.Today.push(conv);
      } else if (date.toDateString() === yesterday.toDateString()) {
        groups.Yesterday.push(conv);
      } else if (date >= lastWeek) {
        groups['Last 7 Days'].push(conv);
      } else {
        groups.Older.push(conv);
      }
    });

    return groups;
  };

  const groupedConversations = groupConversationsByDate();

  const getUniqueSources = (sources: Source[] = []) => {
    const map = new Map<string, Source>();
    sources.forEach((s) => {
      const key = `${s.document}-${s.page}`;
      if (!map.has(key)) map.set(key, s);
    });
    return Array.from(map.values());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim() || loading || !selectedBot.id || !token) return;

    const userMessage: Message = {
      role: 'user',
      content: query,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);

    try {
      const response = await queryBot(token, {
        bot_id: selectedBot.id,
        query,
        top_k: topK,
        enable_web_search: config.supportsWebSearch ? enableWebSearch : false,
        conversation_id: currentConversationId,
      });

      const assistantMessage: Message = {
        role: 'assistant',
        content: response.answer,
        sources: response.sources || [],
        chart: response.chart || null,
        timestamp: new Date().toISOString(),
        isClarification: response.answer.includes('I need some clarification'),
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setCurrentConversationId(response.conversation_id || null);
      void loadConversations();
    } catch (error) {
      if (handleBotAccessError(error)) {
        return;
      }

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Sorry, error processing request.',
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setLoading(false);
      setQuery('');
    }
  };

  const handleFeedback = async (index: number, type: 'like' | 'dislike') => {
    const message = messages[index];
    if (!message || message.role !== 'assistant') return;

    const updatedMessages = [...messages];
    updatedMessages[index] = { ...updatedMessages[index], feedback: type };
    setMessages(updatedMessages);

    if (type === 'like') {
      setShowFeedbackPopup(true);
      setTimeout(() => setShowFeedbackPopup(false), 3000);

      try {
        await axios.post(
          `${API_BASE_URL}/feedback`,
          {
            conversation_id: currentConversationId || '',
            message_index: index,
            feedback_type: 'like',
            feedback_text: null,
            query: messages[index - 1]?.content || '',
            response: message.content,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } catch (error) {
        console.error('Error submitting feedback:', error);
      }
    } else {
      setCurrentFeedbackIndex(index);
      setFeedbackText('');
    }
  };

  const submitDislikeFeedback = async () => {
    if (currentFeedbackIndex === null) return;

    const message = messages[currentFeedbackIndex];

    try {
      await axios.post(
        `${API_BASE_URL}/feedback`,
        {
          conversation_id: currentConversationId || '',
          message_index: currentFeedbackIndex,
          feedback_type: 'dislike',
          feedback_text: feedbackText,
          query: messages[currentFeedbackIndex - 1]?.content || '',
          response: message.content,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setShowFeedbackPopup(true);
      setTimeout(() => setShowFeedbackPopup(false), 3000);

      setCurrentFeedbackIndex(null);
      setFeedbackText('');
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const startEditingMessage = (index: number) => {
    const message = messages[index];
    if (message.role !== 'user') return;

    setEditingMessageIndex(index);
    setEditedQuery(message.content);
  };

  const cancelEditingMessage = () => {
    setEditingMessageIndex(null);
    setEditedQuery('');
  };

  const saveEditedMessage = async () => {
    if (editingMessageIndex === null || !editedQuery.trim() || !selectedBot.id || !token) {
      return;
    }

    const newMessages = messages.slice(0, editingMessageIndex);
    setMessages(newMessages);

    const userMessage: Message = {
      role: 'user',
      content: editedQuery,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);
    setEditingMessageIndex(null);

    try {
      const response = await queryBot(token, {
        bot_id: selectedBot.id,
        query: editedQuery,
        top_k: topK,
        conversation_id: currentConversationId,
        enable_web_search: config.supportsWebSearch ? enableWebSearch : false,
      });

      const assistantMessage: Message = {
        role: 'assistant',
        content: response.answer,
        sources: response.sources || [],
        timestamp: new Date().toISOString(),
        isClarification: response.answer.includes('I need some clarification'),
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setCurrentConversationId(response.conversation_id || null);
      void loadConversations();
    } catch (error) {
      if (handleBotAccessError(error)) {
        return;
      }

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Sorry, error processing request.',
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setLoading(false);
      setEditedQuery('');
    }
  };

  const handleLogout = () => {
    clearSelectedBot();
    keycloak.logout();
  };

  if (!initialized && !initTimeout) {
    return (
      <div className="app">
        <div className="loading-screen">
          <Loader2 className="spin" size={48} />
          <p>Initializing authentication...</p>
          <small style={{ marginTop: '10px', opacity: 0.6 }}>
            This may take a few seconds...
          </small>
        </div>
      </div>
    );
  }

  const IconComponent = config.icon;

  return (
    <div className="app" style={{ '--profile-color': config.color } as React.CSSProperties}>
      {sidebarOpen && window.innerWidth <= 768 && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <button className="new-chat-btn-sidebar" onClick={startNewChat}>
            <Plus size={20} />
            <span>New Chat</span>
          </button>
          <button className="close-sidebar" onClick={() => setSidebarOpen(false)}>
            <X size={20} />
          </button>
        </div>

        <div className="conversations-list">
          {loadingHistory ? (
            <div className="loading-history">
              <Loader2 className="spin" size={24} />
              <p>Loading conversations...</p>
            </div>
          ) : conversations.length === 0 ? (
            <div className="no-conversations">
              <MessageCircle size={48} opacity={0.3} />
              <p>No conversations yet</p>
              <small>Start a new chat to begin</small>
            </div>
          ) : (
            Object.entries(groupedConversations).map(([group, convs]) =>
              convs.length > 0 && (
                <div key={group} className="conversation-group">
                  <div className="group-label">{group}</div>
                  {convs.map((conv) => (
                    <div
                      key={conv.id}
                      className={`conversation-item ${currentConversationId === conv.id ? 'active' : ''}`}
                      onClick={() => loadConversation(conv.id)}
                    >
                      <div className="conversation-icon">
                        <MessageCircle size={16} />
                      </div>
                      <div className="conversation-details">
                        <div className="conversation-title">{conv.title}</div>
                        <div className="conversation-meta">
                          <Clock size={12} />
                          <span>{new Date(conv.last_updated).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      </div>
                      <button
                        className="delete-conversation"
                        onClick={(e) => deleteConversation(conv.id, e)}
                        title="Delete conversation"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )
            )
          )}
        </div>

        <div className="sidebar-footer">
          <div className="user-profile">
            <div className="user-avatar">
              {(keycloak.tokenParsed?.preferred_username || 'U').charAt(0).toUpperCase()}
            </div>
            <div className="user-details">
              <div className="user-name">{keycloak.tokenParsed?.preferred_username || 'User'}</div>
              <div className="user-email">{keycloak.tokenParsed?.email || ''}</div>
            </div>
          </div>
          <button onClick={handleLogout} className="logout-btn-sidebar">
            <LogOut size={16} />
          </button>
        </div>
      </aside>

      <div className="main-content">
        <header className="header">
          <div className="header-left">
            <button className="menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
              <Menu size={24} />
            </button>
            <div className="header-title">
              <IconComponent className="header-icon" />
              <h1>{config.title}</h1>
            </div>
            <button
              className="switch-profile-btn"
              onClick={switchBot}
              title="Switch Assistant"
            >
              <span className="switch-profile-icon-wrap">
                <ArrowLeft size={16} />
              </span>
              <span className="switch-profile-copy">
                <strong>Switch Assistant</strong>
                <small>{config.name}</small>
              </span>
            </button>
          </div>

          <div className="header-controls">
            {config.supportsWebSearch && (
              <div className="web-search-control">
                <input
                  type="checkbox"
                  id="webSearch"
                  checked={enableWebSearch}
                  onChange={(e) => setEnableWebSearch(e.target.checked)}
                  className="web-search-checkbox"
                />
                <label htmlFor="webSearch" className="web-search-label">
                  <Globe size={16} />
                  <span>Deep Search</span>
                </label>
              </div>
            )}
            <div className="top-k-control">
              <label htmlFor="topK">Sources:</label>
              <select
                id="topK"
                value={topK}
                onChange={(e) => setTopK(Number(e.target.value))}
              >
                <option value={2}>2</option>
                <option value={3}>3</option>
                <option value={5}>5</option>
                <option value={10}>10</option>
              </select>
            </div>
          </div>
        </header>

        <main className="main">
          <div className="chat-container">
            {messages.length === 0 ? (
              <div className="welcome">
                <IconComponent className="welcome-icon" />
                <h2>Welcome to {config.title}</h2>
                <p>{config.welcomeMessage}</p>

                <div className="sample-queries">
                  <ul>
                    {config.sampleQueries.map((sampleQuery) => (
                      <li key={sampleQuery} onClick={() => setQuery(sampleQuery)}>
                        {sampleQuery}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <div className="messages">
                {messages.map((message, index) => {
                  const cleanSources = getUniqueSources(message.sources || []);

                  return (
                    <div key={index} className={`message-wrapper ${message.role}`}>
                      <div className="message-container">
                        {message.role === 'assistant' && (
                          <div className="message-avatar">
                            <div className="avatar bot-avatar">
                              <IconComponent size={18} />
                            </div>
                          </div>
                        )}

                        <div className="message-bubble">
                          {message.role === 'user' && (
                            <div className="message-header">
                              <div className="user-badge">
                                <div className="user-avatar-small">
                                  {(keycloak.tokenParsed?.preferred_username || 'U').charAt(0).toUpperCase()}
                                </div>
                                <span className="user-name-small">You</span>
                              </div>
                              <div className="message-time">
                                {new Date(message.timestamp).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                            </div>
                          )}

                          <div className={`message-content ${message.role} ${message.isClarification ? 'clarification' : ''}`}>
                            {message.role === 'user' && editingMessageIndex === index ? (
                              <div className="edit-message-container">
                                <textarea
                                  className="edit-message-input"
                                  value={editedQuery}
                                  onChange={(e) => setEditedQuery(e.target.value)}
                                  rows={3}
                                  autoFocus
                                />
                                <div className="edit-message-actions">
                                  <button className="save-edit-btn" onClick={saveEditedMessage}>
                                    Save & Submit
                                  </button>
                                  <button className="cancel-edit-btn" onClick={cancelEditingMessage}>
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                {message.role === 'assistant' && message.isClarification && (
                                  <div className="clarification-badge">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <circle cx="12" cy="12" r="10"/>
                                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                                      <line x1="12" y1="17" x2="12.01" y2="17"/>
                                    </svg>
                                    <span>Needs Clarification</span>
                                  </div>
                                )}
                                <div className="message-text">{message.content}</div>

                                {message.chart && (
                                  <div className="chart-container">
                                    <ChartRenderer chartData={message.chart} />
                                  </div>
                                )}
                              </>
                            )}

                            {cleanSources.length > 0 && (
                              <div className="sources-section">
                                <button
                                  className="sources-toggle"
                                  onClick={() => toggleSources(index)}
                                >
                                  <FileText size={14} />
                                  <span>View Sources ({cleanSources.length})</span>
                                  <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                                    <path d="M6 8L2 4h8z"/>
                                  </svg>
                                </button>

                                <div id={`sources-${index}`} className="sources-dropdown">
                                  <div className="sources-grid">
                                    {cleanSources.map((source, sourceIndex) => {
                                      const page =
                                        !source.page ||
                                        source.page === "Unknown Page" ||
                                        source.page === "null"
                                          ? null
                                          : source.page;

                                      return (
                                        <div key={sourceIndex} className={`source-card-modern ${source.type === 'web' ? 'web-source' : ''}`}>
                                          <div className="source-card-header">
                                            <div className="source-number">{sourceIndex + 1}</div>
                                            <div className="source-info">
                                              <div className="source-title">
                                                {source.type === 'web' && <Globe size={14} className="web-icon" />}
                                                {source.document}
                                              </div>
                                              {page && (
                                                <div className="source-page">
                                                  <span className="page-icon">📄</span>
                                                  Page {page}
                                                </div>
                                              )}
                                              {source.url && (
                                                <a
                                                  href={source.url}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="source-url"
                                                >
                                                  View Source →
                                                </a>
                                              )}
                                            </div>
                                          </div>
                                          <div className="source-excerpt">{source.snippet}</div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          {message.role === 'assistant' && (
                            <div className="message-footer">
                              <div className="message-time">
                                {new Date(message.timestamp).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                              <div className="message-actions">
                                <button
                                  className={`action-btn feedback-btn ${message.feedback === 'like' ? 'active' : ''}`}
                                  title="Like"
                                  onClick={() => handleFeedback(index, 'like')}
                                  disabled={!!(message.feedback && message.feedback !== 'like')}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill={message.feedback === 'like' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                                    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
                                  </svg>
                                </button>
                                <button
                                  className={`action-btn feedback-btn ${message.feedback === 'dislike' ? 'active' : ''}`}
                                  title="Dislike"
                                  onClick={() => handleFeedback(index, 'dislike')}
                                  disabled={!!(message.feedback && message.feedback !== 'dislike')}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill={message.feedback === 'dislike' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                                    <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/>
                                  </svg>
                                </button>
                                <button
                                  className="action-btn"
                                  title="Copy"
                                  onClick={() => copyToClipboard(message.content)}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                                  </svg>
                                </button>
                              </div>
                            </div>
                          )}

                          {message.role === 'user' && editingMessageIndex !== index && (
                            <div className="message-footer">
                              <button
                                className="edit-btn"
                                title="Edit message"
                                onClick={() => startEditingMessage(index)}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                </svg>
                                Edit
                              </button>
                            </div>
                          )}
                        </div>

                        {message.role === 'assistant' && currentFeedbackIndex === index && (
                          <div className="feedback-form">
                            <textarea
                              className="feedback-textarea"
                              placeholder="Please tell us what went wrong..."
                              value={feedbackText}
                              onChange={(e) => setFeedbackText(e.target.value)}
                              rows={3}
                              autoFocus
                            />
                            <div className="feedback-actions">
                              <button className="submit-feedback-btn" onClick={submitDislikeFeedback}>
                                Submit Feedback
                              </button>
                              <button className="cancel-feedback-btn" onClick={() => setCurrentFeedbackIndex(null)}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {loading && (
                  <div className="message-wrapper assistant">
                    <div className="message-container">
                      <div className="message-avatar">
                        <div className="avatar bot-avatar loading-pulse">
                          <IconComponent size={18} />
                        </div>
                      </div>
                      <div className="message-bubble">
                        <div className="message-content assistant loading-message">
                          <div className="typing-indicator">
                            <span></span>
                            <span></span>
                            <span></span>
                          </div>
                          <span className="loading-text">Analyzing documents...</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="input-wrapper">
            <form onSubmit={handleSubmit} className="query-form">
              <div className="input-container">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={
                    config.id === 'direct-taxation'
                      ? 'Ask about tax calculations, ITR documents, or compliance...'
                      : 'Ask a question about your financial documents...'
                  }
                  disabled={loading}
                  className="query-input"
                />
                <button
                  type="submit"
                  disabled={!query.trim() || loading}
                  className="send-btn"
                >
                  {loading ? <Loader2 className="spin" size={20} /> : <Send size={20} />}
                </button>
              </div>
            </form>
            <div className="footer">
              Powered by <b>Tata Motors Digital.AI Labs</b>
            </div>
          </div>
        </main>
      </div>

      {showFeedbackPopup && (
        <div className="feedback-popup-overlay">
          <div className="feedback-popup">
            <div className="feedback-popup-icon">✓</div>
            <h3>Thank you for your feedback!</h3>
            <p>Your input helps us improve.</p>
          </div>
        </div>
      )}

      {showSwitchConfirm && (
        <div className="confirm-overlay" onClick={() => setShowSwitchConfirm(false)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon">
              <ArrowLeft size={20} />
            </div>
            <h3>Switch assistant?</h3>
            <p>
              Your current chat with <strong>{config.title}</strong> stays saved. You can
              pick it back up any time from the conversation list.
            </p>
            <div className="confirm-actions">
              <button className="confirm-cancel" onClick={() => setShowSwitchConfirm(false)}>
                Cancel
              </button>
              <button className="confirm-proceed" onClick={confirmSwitchBot}>
                Switch assistant
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatInterface;
